<template>
	<div class="DashboardBuildings">
		<div class="DashboardBuildings-Headers is-flex is-justify-content-space-between px-0">
			<ui-text-field v-model="buildingSearchInput" :placeholder="$t('dashboard_buildings.search_placeholder')" />

			<div class=" is-flex is-flex-direction-row-reverse pr-0 is-align-items-center">
				<i
					class="fas ml-2"
					:class="`fa-arrow-${sortOrder === 'desc' ? 'down' : 'up'}`"
					@click="handleSortOrderClick"
				/>

				<select class="select mr-0 pr-0" @change="handleSortSelection">
					<option value="name">{{ $t('dashboard_buildings.sort.name') }}</option>
					<option value="lastRoundHistory">{{ $t('dashboard_buildings.sort.last_check') }}</option>
					<option value="flaws">{{ $t('dashboard_buildings.sort.flaws') }}</option>
				</select>

				<label
					class="is-size-7"
					style="line-height: 2rem; margin-right: .6rem;"
				>
					{{ $t('dashboard_buildings.sort.label') }}
				</label>
			</div>
		</div>

		<div v-for="building in comBuildings"
			:key="building.id"
			@click="setBuilding(building)"
			:class="['DashboardBuildings-Building', { 'DashboardBuildings-Building--active': getActiveBuilding?.id === building.id }]"
		>
			<div class="dashboard-table__row">
				<div class="table-row__name-column">
					<p>
						{{ building.name }}
					</p>
				</div>

				<div class="table-row__information-column">
					<p class="is-size-7" >
						{{ getLatestRunText(building) }}
					</p>
					<p class="is-size-7">
						{{ building.flaws + building.questions }} {{ $t('dashboard_buildings.issues_label') }}
					</p>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
export default {
	props: {
		buildings: {
			type: Array,
			default: () => []
		}
	},

	data: () => ({
		sort: 'name',
		sortOrder: '',
		buildingSearchInput: ''
	}),

	computed: {
		...mapGetters('user', ['getActiveBuilding']),

		 comBuildings() {
			let tempBuildings = [...this.buildings];

			if (this.buildingSearchInput !== '') {
			const search = this.buildingSearchInput.toUpperCase();
			tempBuildings = tempBuildings.filter(x =>
					x.name.toUpperCase().includes(search)
				);
			}

			if (this.sort === 'lastRoundHistory') {
			tempBuildings.sort((a, b) => {
				const aDate = this.getLatestRunDate(a);
				const bDate = this.getLatestRunDate(b);

				const aTime = aDate ? aDate.getTime() : 0;
				const bTime = bDate ? bDate.getTime() : 0;

				return this.sortOrder === 'desc'
				? bTime - aTime
				: aTime - bTime;
			});

			return tempBuildings;
			}

			return this.sortBy(
				tempBuildings,
				`${this.sort}${this.sortOrder === 'desc' ? ' desc' : ''}`
			);
		}

	},

	methods: {
		...mapActions('user', ['setActiveBuilding']),

		setBuilding(building) {
			if (this.getActiveBuilding?.id === building.id)
				return this.setActiveBuilding(null);

			this.setActiveBuilding(building);
		},

		handleSortSelection(e) {
			const column = e.target.value.trim();

			if (this.sort === column)
				this.sortOrder = 'desc';
			else
				this.sortOrder = 'asc';

			this.sort = column;
		},

		handleSortOrderClick() {
			this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
		},
		getLatestRunDate(building) {
			if (!building.lastCompletedRoundHistory && !building.lastNotFullyCompletedRoundHistory) {
			return null; // nothing to sort on
			}

			const lastCompletedRoundHistoryDate = building.lastCompletedRoundHistory
			? new Date(building.lastCompletedRoundHistory)
			: new Date('1900-01-01');

			const lastNotFullyCompletedRoundHistoryDate = building.lastNotFullyCompletedRoundHistory
			? new Date(building.lastNotFullyCompletedRoundHistory)
			: new Date('1900-01-01');

			return lastCompletedRoundHistoryDate > lastNotFullyCompletedRoundHistoryDate
			? lastCompletedRoundHistoryDate
			: lastNotFullyCompletedRoundHistoryDate;
		},

		getLatestRunText(building) {
			if (!building.lastCompletedRoundHistory && !building.lastNotFullyCompletedRoundHistory) {
				return this.$t('dashboard_buildings.not_checked');
			}

			const lastCompletedRoundHistoryDate = building.lastCompletedRoundHistory
				? new Date(building.lastCompletedRoundHistory)
				: new Date('1900-01-01');

			const lastNotFullyCompletedRoundHistoryDate = building.lastNotFullyCompletedRoundHistory
				? new Date(building.lastNotFullyCompletedRoundHistory)
				: new Date('1900-01-01');

			const formatted = this.$utils.date.lastCheckedFormatted(
				lastCompletedRoundHistoryDate > lastNotFullyCompletedRoundHistoryDate
				? building.lastCompletedRoundHistory
				: building.lastNotFullyCompletedRoundHistory
			);

			return lastCompletedRoundHistoryDate > lastNotFullyCompletedRoundHistoryDate
				? this.$t('dashboard_buildings.checked', { date: formatted })
				: this.$t('dashboard_buildings.partially_checked', { date: formatted });
			}
		}
};
</script>

<style lang="scss" scoped>
.DashboardBuildings-Headers {
	padding: 10px;
	flex-wrap: wrap;

	i {
		pointer-events: unset !important;

		&:hover {
			cursor: pointer !important;
		}
	}

	> div {
		margin-bottom: 1rem;

		&:first-child {
			margin-right: 1rem;
			min-width: 150px;
		}

		&:nth-child(2) {
			margin-left: auto;

			> label {
				white-space: nowrap;
			}
		}
	}
}

.DashboardBuildings-Building {
	cursor: pointer;
	padding: 10px;
	border-radius: 4px;
	background-color: #4d535d;
	margin-bottom: 10px;
	box-shadow: rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset;
	transition: background-color 0.3s ease;


	&:hover {
		transition: background-color 0.3s ease;
		background-color: lighten(#4d535d, 10%);
	}
}

.DashboardBuildings-Building--active {
	background-color: #e66f1f;
	&:hover {
		background-color: #e66f1f;
	}
}

.column-header {
	&:hover {
		cursor: pointer;
	}
}
</style>
